import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Share2, Search, User, Users, Check, Loader, UserCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import ConfirmationModal from './ConfirmationModal';
import useConfirm from '../../hooks/useConfirm';
import { handleApiError, notesAPI } from '../../utils/api';

const areSameSelections = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((value, index) => value === sortedB[index]);
};

const ShareNoteModal = ({ isOpen, onClose, note, teamMembers = [], onSuccess }) => {
  const { t } = useLanguage();
  const { confirm, confirmProps } = useConfirm();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentShares, setCurrentShares] = useState([]);

  const loadCurrentShares = useCallback(async () => {
    if (!note?.id) return;

    try {
      const response = await notesAPI.getShares(note.id);
      const shares = response.data?.shares || [];
      const shareUserIds = shares
        .map((share) => Number(share.shared_with_user_id))
        .filter((value) => Number.isInteger(value) && value > 0);

      setCurrentShares(shares);
      setSelectedUsers(shareUserIds);
    } catch (error) {
      setCurrentShares([]);
      setSelectedUsers([]);
      toast.error(handleApiError(error, t('notes.messages.shareError')));
    }
  }, [note?.id, t]);

  useEffect(() => {
    if (!isOpen || !note) return;
    loadCurrentShares();
    setSearchTerm('');
  }, [isOpen, loadCurrentShares, note]);

  const filteredTeamMembers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return teamMembers;
    return teamMembers.filter(
      (member) =>
        member.name?.toLowerCase().includes(term) ||
        member.email?.toLowerCase().includes(term)
    );
  }, [searchTerm, teamMembers]);

  const currentShareIds = useMemo(
    () =>
      currentShares
        .map((share) => Number(share.shared_with_user_id))
        .filter((value) => Number.isInteger(value) && value > 0),
    [currentShares]
  );

  const hasSelectionChanged = useMemo(
    () => !areSameSelections(selectedUsers, currentShareIds),
    [selectedUsers, currentShareIds]
  );

  const toggleUserSelection = (userId) => {
    setSelectedUsers((previous) =>
      previous.includes(userId)
        ? previous.filter((id) => id !== userId)
        : [...previous, userId]
    );
  };

  const handleShare = async () => {
    if (selectedUsers.length === 0) {
      toast.error(t('notes.messages.selectUsers'));
      return;
    }

    setLoading(true);
    try {
      await notesAPI.share(note.id, selectedUsers);
      toast.success(t('notes.messages.shareSuccess'));
      await onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(handleApiError(error, t('notes.messages.shareError')));
    } finally {
      setLoading(false);
    }
  };

  const handleUnshare = async () => {
    const confirmed = await confirm({
      title: t('notes.shareModal.stopSharingConfirm.title'),
      message: t('notes.shareModal.stopSharingConfirm.message'),
      type: 'warning',
      confirmText: t('notes.shareModal.stopSharingConfirm.confirmText'),
      cancelText: t('notes.shareModal.stopSharingConfirm.cancelText')
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      await notesAPI.unshare(note.id);
      toast.success(t('notes.messages.unshareSuccess'));
      await onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(handleApiError(error, t('notes.messages.unshareError')));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen || !note) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 18 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 18 }}
        className="w-full max-w-2xl max-h-[92vh] rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden"
      >
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
          <div className="min-w-0">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
              <Share2 className="w-4 h-4" />
            </div>
            <h2 className="mt-3 text-lg font-semibold text-gray-900">{t('notes.shareModal.title')}</h2>
            <p className="mt-1 text-sm text-gray-600 truncate">{note.title}</p>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="rounded-md p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="overflow-y-auto max-h-[calc(92vh-180px)] p-6 space-y-5">
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <label className="block text-xs font-semibold text-gray-500 mb-2">
              {t('notes.shareModal.searchPlaceholder')}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('notes.shareModal.searchPlaceholder')}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                disabled={loading}
              />
            </div>
          </section>

          {currentShares.length > 0 && (
            <section className="rounded-lg border border-gray-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {t('notes.shareModal.currentlyShared')}
              </h3>
              <div className="space-y-2">
                {currentShares.map((share) => (
                  <div
                    key={share.id}
                    className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                      <UserCheck className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {share.shared_with_name}
                      </p>
                      <p className="truncate text-xs text-gray-500">{share.shared_with_email}</p>
                    </div>
                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
                      {t('notes.shareModal.shared')}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                {t('notes.shareModal.teamMembers')} ({filteredTeamMembers.length})
              </h3>
              {selectedUsers.length > 0 && (
                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                  <strong>{selectedUsers.length}</strong>{' '}
                  {selectedUsers.length > 1
                    ? t('notes.shareModal.selectedPlural')
                    : t('notes.shareModal.selected')}
                </span>
              )}
            </div>

            {filteredTeamMembers.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 py-10 text-center text-gray-500">
                <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">
                  {searchTerm ? t('notes.shareModal.noMembersFound') : t('notes.shareModal.noMembers')}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {filteredTeamMembers.map((member) => {
                  const isSelected = selectedUsers.includes(member.id);
                  return (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => !loading && toggleUserSelection(member.id)}
                      className={`w-full flex items-center gap-3 rounded-lg border px-3 py-3 text-left transition ${
                        isSelected
                          ? 'border-indigo-200 bg-indigo-50'
                          : 'border-gray-200 bg-white hover:border-indigo-200'
                      } ${loading ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                        <User className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="truncate text-sm font-medium text-gray-900">{member.name}</p>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              member.role === 'admin'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {member.role === 'admin'
                              ? t('dashboard.roles.admin')
                              : t('dashboard.roles.hr')}
                          </span>
                        </div>
                        <p className="truncate text-xs text-gray-500 mt-0.5">{member.email}</p>
                      </div>
                      <div
                        className={`h-5 w-5 rounded-md border-2 flex items-center justify-center ${
                          isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        <footer className="flex flex-col-reverse gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {currentShares.length > 0 && (
              <button
                onClick={handleUnshare}
                disabled={loading}
                className="inline-flex items-center rounded-lg border border-red-300 bg-red-100 px-3.5 py-2 text-sm font-medium text-red-700 transition hover:bg-red-200 disabled:opacity-50"
              >
                {t('notes.shareModal.stopSharing')}
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              disabled={loading}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            >
              {t('notes.shareModal.cancel')}
            </button>
            <button
              onClick={handleShare}
              disabled={loading || selectedUsers.length === 0 || !hasSelectionChanged}
              className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader className="mr-2 h-4 w-4 animate-spin" />
                  {t('notes.shareModal.sharing')}
                </>
              ) : (
                <>
                  <Share2 className="mr-2 h-4 w-4" />
                  {t('notes.shareModal.share')}
                </>
              )}
            </button>
          </div>
        </footer>
      </motion.div>

      <ConfirmationModal {...confirmProps} />
    </div>
  );
};

export default ShareNoteModal;
